import { Injectable } from '@angular/core';
import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { Observable, map, of } from 'rxjs';
import { TableColumn } from '../models/table.model';
import {
  FilterTab,
  SortOption,
  PaginationInfo,
  LetterData,
  LettersFilterState,
  AuditsData,
  LetterDetails,
  LettersResponse,
  LettersJsonData,
} from '../models/letters.model';
import { environment } from '../../environments/environment';
import { formatDateDDMMYYYY } from '../utils/date.util';

@Injectable({
  providedIn: 'root',
})
export class LettersService {
  private apiUrl = `${environment.apiUrl}/Letters`;

  constructor(private http: HttpClient) {}

  /**
   * Parse date string in DD-MM-YYYY format to Date object
   */
  private parseDate(dateString: string): Date {
    const [day, month, year] = dateString.split('-').map(Number);
    return new Date(year, month - 1, day); // month is 0-indexed in Date constructor
  }

  /**
   * Get source name from source ID
   */
  private getSourceName(sourceId: number): string {
    switch (sourceId) {
      case 1:
        return 'Internal';
      case 2:
        return 'External';
      default:
        return 'Unknown';
    }
  }

  /**
   * Get status name from status code
   */
  private getStatusName(status: number): string {
    switch (status) {
      case 1:
        return 'Pending';
      case 2:
        return 'Responded';
      case 3:
        return 'Archived';
      default:
        return 'Unknown';
    }
  }

  /**
   * Get file type from file name
   */
  private getFileTypeFromFileName(fileName: string): string {
    if (!fileName) return 'Unknown';
    const extension = fileName.split('.').pop()?.toLowerCase();
    switch (extension) {
      case 'pdf':
        return 'PDF';
      case 'doc':
      case 'docx':
        return 'DOC';
      default:
        return extension?.toUpperCase() || 'Unknown';
    }
  }

  getLettersData(): Observable<LetterData[]> {
    // Use the same API endpoint with default parameters
    return this.getLettersWithFilters({ page: 1, perPage: 100 }).pipe(
      map((response) => response.data)
    );
  }

  getLettersWithFilters(
    filters: {
      filter?: string;
      search?: string;
      sortBy?: string;
      page?: number;
      perPage?: number;
      showDeleted?: boolean;
    } = {}
  ): Observable<LettersResponse> {
    // Build query parameters
    let params = new HttpParams();

    // Pagination parameters
    const pageNumber = filters.page || 1;
    const pageSize = filters.perPage || 10;
    params = params.set('pageNumber', pageNumber.toString());
    params = params.set('pageSize', pageSize.toString());

    // Sorting parameter
    if (filters.sortBy) {
      params = params.set('sortBy', filters.sortBy);
    } else {
      params = params.set('sortBy', 'newest'); // Default sorting
    }

    // Filter parameter (replaces status)
    if (filters.filter) {
      params = params.set('filter', filters.filter);
    } else {
      params = params.set('filter', 'all'); // Default filter
    }

    // Search filter
    if (filters.search) {
      params = params.set('search', filters.search);
    }

    // Show deleted filter
    if (filters.showDeleted) {
      params = params.set('showDeleted', 'true');
    }

    const apiUrl = `${this.apiUrl}/getall`;
    return this.http.get<any>(apiUrl, { params }).pipe(
      map((response) => {
        // Transform API response items to match LetterData interface
        const transformedData = (response.items || []).map((item: any) => ({
          id: item.id,
          uploadDate: formatDateDDMMYYYY(item.createdOn),
          createdOn: item.createdOn,
          documentName: item.documentName,
          source: this.getSourceName(item.sourceId),
          assignedDate: item.assignedDate || item.createdOn || null, // Pass raw date to let table component format it
          dueDate: formatDateDDMMYYYY(item.dueDate),
          status: this.getStatusName(item.status),
          assignedTo: 'N/A', // Not provided in API response
          priority: 'Medium', // Default value
          category: 'General', // Default value
          fileSize: 'N/A', // Not provided in API response
          fileType: this.getFileTypeFromFileName(item.uploadedFileName),
          responseDate: item.responseDate || '',
          respondedBy: item.respondedBy || '',
          responseSummary: item.responseSummary || '',
          approveRejectStatus: item.approveRejectStatus || 3, // Default to 3 (pending) if not provided
        }));

        return {
          data: transformedData,
          total: response.totalCount || 0,
          page: response.pageNumber || pageNumber,
          perPage: response.pageSize || pageSize,
        };
      })
    );
  }

  getLetterById(id: string): Observable<any> {
    const apiUrl = `${this.apiUrl}/${id}/workflow`;
    return this.http.get<any>(apiUrl);
  }

  /**
   * Approve or reject a letter
   * @param id - Letter ID (documentId)
   * @param isApproved - Whether to approve (true) or reject (false)
   * @param comment - Comment for the approval/rejection
   * @returns Observable<any>
   */
  approveRejectLetter(
    id: string,
    isApproved: boolean,
    comment: string
  ): Observable<any> {
    const body = {
      module: 1,
      documentId: id,
      isApproved: isApproved,
      approveRejectComment: comment,
    };

    return this.http.post<any>(`${this.apiUrl}/approvereject`, body, {
      headers: {
        'Content-Type': 'application/json',
      },
    });
  }

  getAuditLog(documentId: string): Observable<any[]> {
    const baseUrl = environment.apiUrl.replace('/api', '');
    const apiUrl = `${baseUrl}/${documentId}/getauditlog`;
    return this.http.get<any[]>(apiUrl);
  }

  getFilterTabs(): Observable<FilterTab[]> {
    // Get real data from API to calculate counts
    return this.getLettersWithFilters({ page: 1, perPage: 1000 }).pipe(
      map((response) => {
        const allCount = response.data.length;
        const pendingCount = response.data.filter(
          (item) => item.status === 'Pending'
        ).length;
        const respondedCount = response.data.filter(
          (item) => item.status === 'Responded'
        ).length;

        return [
          { id: 'all', label: 'All', count: allCount },
          { id: 'pending', label: 'Pending', count: pendingCount },
          { id: 'responded', label: 'Responded', count: respondedCount },
        ];
      })
    );
  }

  getSortOptions(): Observable<SortOption[]> {
    // Return only Newest and Oldest options as requested
    return of([
      { value: 'newest', label: 'Newest First' },
      { value: 'oldest', label: 'Oldest First' },
    ]);
  }

  getPaginationInfo(): Observable<PaginationInfo> {
    // Return default pagination info
    return of({
      currentPage: 1,
      totalItems: 0,
      totalPages: 1,
      itemsPerPage: 10,
      startItem: 1,
      endItem: 10,
    });
  }

  getTableColumns(): Observable<TableColumn[]> {
    // Return hardcoded table columns since these are UI configuration
    // Note: Actions column is automatically added by the table component
    return of([
      { key: 'uploadDate', label: 'Date', sortable: true },
      { key: 'documentName', label: 'Document Name', sortable: true },
      { key: 'source', label: 'Source', sortable: true },
      { key: 'assignedDate', label: 'Assigned Date', sortable: true },
      { key: 'dueDate', label: 'Due Date', sortable: true },
      { key: 'status', label: 'Status', sortable: true }, //Approval Status column
    ]);
  }

  getAuditsData(): Observable<AuditsData[]> {
    // This would typically come from a separate audit API endpoint
    return this.http.get<AuditsData[]>(`${this.apiUrl}/audits`);
  }

  createLetter(formData: FormData): Observable<any> {
    const createApiUrl = `${environment.apiUrl}/Letters/create`;
    return this.http.post<any>(createApiUrl, formData);
  }

  /**
   * Update an existing letter using multipart/form-data.
   * Keeps backward compatibility with existing upload component flow.
   */
  updateLetterMultipart(id: string, formData: FormData): Observable<any> {
    return this.http.put<any>(`${this.apiUrl}/${id}/update`, formData);
  }

  updateLetter(
    id: string,
    letter: Partial<LetterData>
  ): Observable<LetterData> {
    // For local JSON approach, we'll simulate the update
    // In a real scenario, you would update the JSON file or use a backend
    const updatedLetter: LetterData = {
      id,
      uploadDate: letter.uploadDate || '',
      documentName: letter.documentName || '',
      source: letter.source || '',
      assignedDate: letter.assignedDate || '',
      dueDate: letter.dueDate || '',
      status: letter.status || 'Pending',
      assignedTo: letter.assignedTo || '',
      priority: letter.priority || '',
      category: letter.category || '',
      fileSize: letter.fileSize || '',
      fileType: letter.fileType || '',
    };

    return of(updatedLetter);
  }

  /**
   * Get assignable users for letter assignment
   */
  getAssignableUsers(): Observable<{ id: number; name: string }[]> {
    const apiUrl = `${environment.apiUrl}/Users/assignableusers`;

    return this.http.get<any[]>(apiUrl).pipe(
      map((users) => {
        // Transform the API response to match the expected format
        const transformedUsers = users.map((user) => ({
          id: user.id,
          name:
            user.name ||
            user.fullName ||
            `${user.firstName || ''} ${user.lastName || ''}`.trim(),
        }));
        return transformedUsers;
      })
    );
  }

  /**
   * Get active sources for letter assignment
   */
  getActiveSources(): Observable<{ id: number; name: string }[]> {
    const apiUrl = `${environment.apiUrl}/Sources/activesources`;

    return this.http.get<any[]>(apiUrl).pipe(
      map((sources) => {
        // Transform the API response to match the expected format
        const transformedSources = sources.map((source) => ({
          id: source.id,
          name: source.name || source.sourceName || source.title,
        }));
        return transformedSources;
      })
    );
  }

  // Delete letter
  deleteLetter(letterId: string): Observable<any> {
    return this.http.delete<any>(`${this.apiUrl}/${letterId}/delete`);
  }

  /**
   * Download a letter document
   * @param id - Letter ID
   * @returns Observable<{blob: Blob, filename: string}>
   */
  downloadLetterDocument(
    id: string
  ): Observable<{ blob: Blob; filename: string; type: string }> {
    return this.http
      .get(`${this.apiUrl}/${id}/download`, {
        responseType: 'blob',
        observe: 'response',
      })
      .pipe(
        map((response: HttpResponse<Blob>) => {
          // Extract filename from content-disposition header
          const contentDisposition = response.headers.get(
            'content-disposition'
          );
          let filename = `${id}.pdf`; // Default filename

          if (contentDisposition) {
            const filenameMatch = contentDisposition.match(
              /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/
            );
            if (filenameMatch && filenameMatch[1]) {
              filename = filenameMatch[1].replace(/['"]/g, '');
            }
          }

          // Extract MIME type from content-type header
          const contentType =
            response.headers.get('content-type') || 'application/pdf';

          // Ensure filename extension matches the MIME type
          if (
            contentType ===
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
          ) {
            // If it's a .docx file, ensure the filename has .docx extension
            if (!filename.toLowerCase().endsWith('.docx')) {
              filename = filename.replace(/\.[^/.]+$/, '') + '.docx';
            }
          } else if (contentType === 'application/msword') {
            // If it's a .doc file, ensure the filename has .doc extension
            if (!filename.toLowerCase().endsWith('.doc')) {
              filename = filename.replace(/\.[^/.]+$/, '') + '.doc';
            }
          } else if (contentType === 'application/pdf') {
            // If it's a PDF file, ensure the filename has .pdf extension
            if (!filename.toLowerCase().endsWith('.pdf')) {
              filename = filename.replace(/\.[^/.]+$/, '') + '.pdf';
            }
          }

          return {
            blob: response.body!,
            filename: filename,
            type: contentType,
          };
        })
      );
  }

  /**
   * Get reply-to users list
   * @returns Observable<{id: string, fullName: string}[]>
   */
  getReplyToUsersList(): Observable<{ id: string; fullName: string }[]> {
    const apiUrl = `${this.apiUrl}/replytouserslist`;
    return this.http.get<{ id: string; fullName: string }[]>(apiUrl);
  }

  /**
   * Reply to a letter
   * @param replyData - Reply data containing letterId, replySubject, replyDate, replyTime, replyTo, sender
   * @returns Observable<any>
   */
  replyLetter(replyData: {
    letterId: string;
    replySubject: string;
    replyDate: string;
    replyTime: string;
    replyTo: string;
    sender: string;
  }): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/api/replyletter`, replyData);
  }
}
